import React from 'react';
import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { ImgMic } from './Icons/ImgMic';

type VoiceRecorderProps = {
  language?: string;
  onTranscription: (text: string) => void;
};

@observer
class VoiceRecorder extends React.Component<VoiceRecorderProps> {
  @observable private _isRecording = false;
  @observable private _isProcessing = false;
  @observable private _transcription = '';
  @observable private _error = '';
  @observable private _showErrorPopup = false;
  @observable private _isRequestingPermission = false;

  private _mediaRecorder: MediaRecorder | null = null;
  private _audioChunks: Blob[] = [];
  private _errorTimeout: number | null = null;
  private _abortController: AbortController | null = null;

  constructor(props: VoiceRecorderProps) {
    super(props);
    makeObservable(this);
  }

  componentWillUnmount() {
    this._cleanup();
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
    }
  }

  @computed get statusClass(): string {
    if (this._error) return 'error-text';
    if (this._transcription && !this._isRecording && !this._isProcessing) {
      return 'success-text';
    }
    return '';
  }

  @action.bound
  private _setError(message: string) {
    this._error = message;
    this._showErrorPopup = true;

    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
    }

    this._errorTimeout = setTimeout(() => {
      runInAction(() => {
        this._error = '';
        this._showErrorPopup = false;
      });
    }, 2000);
  }

  @action.bound
  private _setTranscription(value: string) {
    this._transcription = value;
    this.props.onTranscription(value);
  }

  @action.bound
  private _cleanup() {
    if (this._mediaRecorder && this._mediaRecorder.state !== 'inactive') {
      this._mediaRecorder.stop();
    }
    if (this._mediaRecorder?.stream) {
      this._mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
    this._mediaRecorder = null;
    this._audioChunks = [];
    
    if (this._abortController) {
      this._abortController.abort();
      this._abortController = null;
    }
  }

  @action.bound
  async _startRecording() {
    // Clear any previous errors
    this._error = '';
    this._showErrorPopup = false;

    // Provide haptic feedback if supported
    await new Promise<void>(resolve => {
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
        setTimeout(resolve, 250);
      } else {
        resolve();
      }
    });

    // Check if recording was cancelled during haptic feedback
    if (!this._isRecording) {
      return;
    }

    try {
      runInAction(() => {
        this._isRequestingPermission = true;
      });

      const constraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          channelCount: 1,
          volume: 1.0
        },
        video: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      runInAction(() => {
        this._isRequestingPermission = false;
      });

      // Check if recording was cancelled while requesting permission
      if (!this._isRecording) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      this._mediaRecorder = mediaRecorder;
      this._audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this._audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(this._audioChunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Only send to Whisper if we have audio data and weren't cancelled
        if (this._audioChunks.length > 0 && this._isProcessing) {
          await this.sendToWhisper(audioBlob);
        }
      };

      mediaRecorder.start();

    } catch (err) {
      runInAction(() => {
        this._isRecording = false;
        this._isRequestingPermission = false;
        this._isProcessing = false;
      });

      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        this._setError('Microphone permission denied');
      } else if (err instanceof DOMException && err.name === 'NotFoundError') {
        this._setError('No microphone found');
      } else {
        this._setError(err instanceof Error ? err.message : 'Error accessing microphone');
      }
      console.error('Error accessing microphone:', err);
    }
  }

  @action.bound
  private _stopRecording() {
    if (this._mediaRecorder && this._isRecording) {
      this._mediaRecorder.stop();
      this._isRecording = false;
      this._isProcessing = true;
    }
    
    navigator.vibrate?.([100]);
  }

  @action.bound
  private _cancelProcessing() {
    if (this._isProcessing) {
      if (this._abortController) {
        this._abortController.abort();
      }
      
      this._cleanup();
      
      runInAction(() => {
        this._isProcessing = false;
        this._transcription = '';
      });

      navigator.vibrate?.([50, 50, 50]);
    }
  }

  @action
  async sendToWhisper(audioBlob: Blob) {
    try {
      this._abortController = new AbortController();

      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');
      formData.append('model', 'whisper-1');
      formData.append('content_type', 'audio/webm');

      const lang = this.props.language || 'auto';
      
      const response = await fetch(`/api/transcribe?lang=${encodeURIComponent(lang)}`, {
        method: 'POST',
        body: formData,
        signal: this._abortController.signal
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const data = await response.json();

      runInAction(() => {
        this._transcription = data.text || data.transcription || '';
        this._setTranscription(this._transcription);
        this._isProcessing = false;
      });
      
    } catch (err) {
      // Don't show error if request was aborted (user cancelled)
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      runInAction(() => {
        this._isProcessing = false;
      });
      this._setError('Failed to transcribe audio. Configure your backend endpoint.');
      console.error('Transcription error:', err);
    } finally {
      this._abortController = null;
    }
  }

  _onMouseDownClick = () => {
    runInAction(() => {
      this._isRecording = true;
    });
    this._startRecording();
  }

  _onMouseUpClick = () => {
    if (this._isRecording) {
      this._stopRecording();
    }
  }

  _onButtonClick = () => {
    if (this._isProcessing) {
      this._cancelProcessing();
    }
  }

  handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    runInAction(() => {
      this._isRecording = true;
    });
    this._startRecording();
  }

  handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    if (this._isRecording) {
      this._stopRecording();
    }
  }

  render() {
    const isSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      && MediaRecorder.isTypeSupported('audio/webm');

    if (!isSupported) {
      return null;
    }

    return (
      <div style={{ 
        margin: '0 auto',
        position: 'relative'
      }}>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.15);
              opacity: 0.8;
            }
          }

          @keyframes ripple {
            0% {
              transform: scale(1);
              opacity: 0.6;
            }
            100% {
              transform: scale(2.5);
              opacity: 0;
            }
          }

          @keyframes wave {
            0% {
              transform: scale(1);
              opacity: 0.6;
            }
            100% {
              transform: scale(2);
              opacity: 0;
            }
          }

          @keyframes colorShift {
            0%, 100% {
              border-color: #ff9500;
            }
            25% {
              border-color: #ff6b00;
            }
            50% {
              border-color: #ffa800;
            }
            75% {
              border-color: #ff8500;
            }
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes fadeOut {
            from {
              opacity: 1;
            }
            to {
              opacity: 0;
            }
          }

          .voice-button {
            position: relative;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            border: 2px solid var(--border-color);
            background: var(--bg-secondary);
            color: var(--text-primary);
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            user-select: none;
            -webkit-user-select: none;
            touch-action: none;
          }

          .voice-button:hover {
            background: var(--hover-bg);
            border-color: var(--accent-color);
          }

          .voice-button:active {
            transform: scale(0.95);
          }

          .voice-button.recording {
            background: var(--accent-color);
            border-color: var(--accent-color);
            animation: pulse 1.5s ease-in-out infinite;
          }

          .voice-button.processing {
            background: transparent;
            border-color: #ff9500;
            border-width: 3px;
            cursor: pointer;
            animation: colorShift 2s ease-in-out infinite;
          }

          .voice-button.processing:hover {
            background: rgba(255, 149, 0, 0.1);
          }

          .voice-button.error {
            background: #ff4444;
            border-color: #ff4444;
            animation: pulse 0.5s ease-in-out 2;
          }

          .voice-button.requesting-permission {
            border-color: #ffaa00;
            cursor: wait;
            opacity: 0.7;
          }

          .ripple-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            overflow: visible;
          }

          .ripple {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 44px;
            height: 44px;
            margin-left: -22px;
            margin-top: -22px;
            border-radius: 50%;
            border: 2px solid var(--accent-color);
            animation: ripple 1.5s ease-out infinite;
          }

          .ripple:nth-child(2) {
            animation-delay: 0.5s;
          }

          .ripple:nth-child(3) {
            animation-delay: 1s;
          }

          .processing-wave {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 30px;
            height: 30px;
            margin-left: -15px;
            margin-top: -15px;
            border-radius: 50%;
            border: 2px solid #ff9500;
            pointer-events: none;
          }

          .processing-wave:nth-child(1) {
            animation: wave 1.2s ease-out infinite;
          }

          .processing-wave:nth-child(2) {
            animation: wave 1.2s ease-out infinite 0.3s;
          }

          .processing-wave:nth-child(3) {
            animation: wave 1.2s ease-out infinite 0.6s;
          }

          .processing-wave:nth-child(4) {
            animation: wave 1.2s ease-out infinite 0.9s;
          }

          .prompt-container {
            display: flex;
            gap: 8px;
            align-items: flex-end;
          }

          .error-popup {
            position: absolute;
            bottom: calc(100% + 12px);
            left: 50%;
            transform: translateX(-50%);
            background: #ff4444;
            color: white;
            padding: 8px 16px;
            border-radius: 8px;
            font-size: 13px;
            white-space: nowrap;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            animation: slideDown 0.2s ease-out;
            z-index: 1000;
          }

          .error-popup::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 6px solid transparent;
            border-top-color: #ff4444;
          }

          .error-popup.hiding {
            animation: fadeOut 0.2s ease-out forwards;
          }
        `}</style>

        <div className="prompt-container">
          <button
            className={`voice-button ${this._isRecording ? 'recording' : ''} ${this._isProcessing ? 'processing' : ''} ${this._error ? 'error' : ''} ${this._isRequestingPermission ? 'requesting-permission' : ''}`}
            onMouseDown={this._isProcessing ? undefined : this._onMouseDownClick}
            onMouseUp={this._isProcessing ? undefined : this._onMouseUpClick}
            onMouseLeave={this._isProcessing ? undefined : this._stopRecording}
            onTouchStart={this._isProcessing ? undefined : this.handleTouchStart}
            onTouchEnd={this._isProcessing ? undefined : this.handleTouchEnd}
            onClick={this._onButtonClick}
            disabled={this._isRequestingPermission}
            title={
              this._isProcessing 
                ? 'Transcribing... Click to cancel' 
                : this._isRecording 
                ? 'Recording... Release to stop' 
                : this._isRequestingPermission
                ? 'Requesting microphone permission...'
                : 'Hold to record'
            }
          >
            <ImgMic />
            {this._isRecording && (
              <div className="ripple-container">
                <div className="ripple" />
                <div className="ripple" />
                <div className="ripple" />
              </div>
            )}
            {this._isProcessing && (
              <div className="ripple-container">
                <div className="processing-wave" />
                <div className="processing-wave" />
                <div className="processing-wave" />
                <div className="processing-wave" />
              </div>
            )}
          </button>

          {this._showErrorPopup && this._error && (
            <div className="error-popup">
              {this._error}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default VoiceRecorder;